import React from 'react';
import {
  Box,
  Heading,
  Text,
  VStack,
  HStack,
  Card,
  CardBody,
  CardHeader,
  Badge,
  Button,
  useDisclosure
} from '@chakra-ui/react';
import { theme as mythboundTheme } from '../config/theme';
import { useAuth } from '../context/AuthContext';
import ScreenNameModal from '../components/ScreenNameModal';

/**
 * Profile Page Component
 * 
 * Displays user profile information including screen name management,
 * account details, and settings.
 */

const Profile: React.FC = () => {
  const { userInfo } = useAuth();
  const { isOpen: isScreenNameModalOpen, onOpen: onScreenNameModalOpen, onClose: onScreenNameModalClose } = useDisclosure();

  return (
    <Box>
      <Heading 
        fontFamily={mythboundTheme.typography.fontFamily.heading}
        color={mythboundTheme.colors.primary.gold}
        mb={6}
      >
        Profile
      </Heading>

      <VStack spacing={6} align="stretch">
        {/* User Information Card */}
        <Card
          bg={mythboundTheme.colors.secondary.parchment}
          color={mythboundTheme.colors.accent.brown}
          borderRadius="md"
          boxShadow={mythboundTheme.shadows.md}
          borderWidth="1px"
          borderColor={mythboundTheme.colors.primary.gold}
        >
          <CardHeader>
            <Heading size="md" fontFamily={mythboundTheme.typography.fontFamily.heading}>
              Account Information
            </Heading>
          </CardHeader>
          <CardBody>
            <VStack spacing={4} align="stretch">
              <HStack justify="space-between">
                <Text fontWeight="medium">Screen Name:</Text>
                <HStack>
                  <Text
                    title={userInfo?.screenName ? (userInfo?.screenNameId ? `${userInfo.screenName}#${userInfo.screenNameId.slice(-4)}` : userInfo.screenName) : undefined}
                  >
                    {userInfo?.screenName || 'Not set'}
                  </Text>
                  <Button 
                    size="sm" 
                    variant="outline"
                    colorScheme="blue"
                    onClick={onScreenNameModalOpen}
                  >
                    {userInfo?.screenName ? 'Change' : 'Set Screen Name'}
                  </Button>
                </HStack>
              </HStack>
              
              <HStack justify="space-between">
                <Text fontWeight="medium">Username:</Text>
                <Text>{userInfo?.username || 'N/A'}</Text>
              </HStack>
              
              <HStack justify="space-between">
                <Text fontWeight="medium">User ID:</Text>
                <Text fontFamily="monospace" fontSize="sm">{userInfo?.id || 'N/A'}</Text>
              </HStack>
              
              <HStack justify="space-between">
                <Text fontWeight="medium">Role:</Text>
                <Badge 
                  colorScheme={userInfo?.roles?.includes('admin') ? 'red' : 'blue'}
                  borderRadius="full"
                  px={2}
                >
                  {userInfo?.roles?.[0] || 'User'}
                </Badge>
              </HStack>
            </VStack>
          </CardBody>
        </Card>

        {/* Screen Name Management Card */}
        {userInfo?.screenName && (
          <Card
            bg={mythboundTheme.colors.secondary.parchment}
            color={mythboundTheme.colors.accent.brown}
            borderRadius="md"
            boxShadow={mythboundTheme.shadows.md}
            borderWidth="1px"
            borderColor={mythboundTheme.colors.primary.gold}
          >
            <CardHeader>
              <Heading size="md" fontFamily={mythboundTheme.typography.fontFamily.heading}>
                Screen Name Details
              </Heading>
            </CardHeader>
            <CardBody>
              <VStack spacing={4} align="stretch">
                <HStack justify="space-between">
                  <Text fontWeight="medium">Can Change Screen Name:</Text>
                  <Text>{userInfo.canChangeScreenName ? 'Yes' : 'No'}</Text>
                </HStack>
                
                <HStack justify="space-between">
                  <Text fontWeight="medium">Changes Made:</Text>
                  <Text>{userInfo.screenNameChangeCount || 0}</Text>
                </HStack>
                
                {!userInfo.canChangeScreenName && (
                  <Text fontSize="sm" color={mythboundTheme.colors.accent.silver}>
                    You have reached your screen name change limit for your current account tier.
                  </Text>
                )}
              </VStack>
            </CardBody>
          </Card>
        )}

        {/* Settings Card - Placeholder for future features */}
        <Card
          bg={mythboundTheme.colors.secondary.parchment}
          color={mythboundTheme.colors.accent.brown}
          borderRadius="md"
          boxShadow={mythboundTheme.shadows.md}
          borderWidth="1px"
          borderColor={mythboundTheme.colors.primary.gold}
        >
          <CardHeader>
            <Heading size="md" fontFamily={mythboundTheme.typography.fontFamily.heading}>
              Settings
            </Heading>
          </CardHeader>
          <CardBody>
            <Text color={mythboundTheme.colors.accent.silver}>
              Additional profile settings will be available here in future updates.
            </Text>
          </CardBody>
        </Card>
      </VStack>

      {/* Screen Name Modal */}
      <ScreenNameModal 
        isOpen={isScreenNameModalOpen}
        onClose={onScreenNameModalClose}
      />
    </Box>
  );
};

export default Profile;